<script setup lang="ts">
import { nextTick, onMounted, ref, useId } from 'vue';
import { ModalTransition } from '../modal-transition';

// The shared "ask for a name" modal behind NewFolderModal, RenameModal and CopyModal.
//
// Those three were byte-identical markup, styles and submit logic, differing only in their strings,
// their emit name and their initial value. RENAME_ITEM_PLAN.md deliberately kept the first two apart
// ("not generalized with NewFolderModal"), which was the right call at two consumers; copy makes
// three, so the shared part now lives here and each keeps a thin wrapper of its own.
//
// Purely presentational: it takes RESOLVED STRINGS, not i18n key paths, so it never imports
// vue-i18n, tests with plain props, and a mistyped key fails in the wrapper that owns it rather than
// silently rendering a raw key path (this app's fallbackLocale is its only locale, so a missing key
// renders verbatim).

const props = defineProps<{
  title: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  cancelLabel: string;
  closeAriaLabel: string;
  /** Pre-fills the input. Empty for a create-style modal, a base name for rename/copy. */
  initialValue?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'submit', name: string): void;
}>();

const name = ref(props.initialValue ?? '');
const inputRef = ref<HTMLInputElement>();
// Three instances of this component can exist in one FileManager, so the label/input pairing cannot
// use a hardcoded id the way the separate components used to.
const inputId = useId();

onMounted(async () => {
  await nextTick();
  inputRef.value?.focus();
  // Always selects, with no prop to opt out: the create case starts empty, where select() is a no-op.
  // For rename/copy it makes replacing the whole name a single keystroke, the standard OS convention.
  inputRef.value?.select();
});

const submit = () => {
  const trimmedName = name.value.trim();
  if (!trimmedName) return;
  emit('submit', trimmedName);
  emit('close');
};
</script>

<template>
  <ModalTransition>
    <div class="modal is-active" role="dialog" aria-modal="true">
      <div class="modal-background" @click="emit('close')"></div>

      <div class="modal-card name-form-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ title }}</p>
          <button class="delete" :aria-label="closeAriaLabel" type="button" @click="emit('close')"></button>
        </header>

        <section class="modal-card-body">
          <div class="field">
            <label class="label" :for="inputId">{{ label }}</label>
            <div class="control">
              <input
                :id="inputId"
                ref="inputRef"
                v-model="name"
                class="input"
                type="text"
                :placeholder="placeholder"
                @keyup.enter="submit"
              />
            </div>
          </div>
        </section>

        <footer class="modal-card-foot actions">
          <button type="button" class="button is-light" @click="emit('close')">
            {{ cancelLabel }}
          </button>
          <button type="button" class="button is-primary" :disabled="!name.trim()" @click="submit">
            {{ submitLabel }}
          </button>
        </footer>
      </div>
    </div>
  </ModalTransition>
</template>

<style scoped lang="scss">
.name-form-modal-card {
  width: min(420px, 92vw);

  .modal-card-head,
  .modal-card-foot {
    padding: 0.75rem 1.5rem;
  }

  .modal-card-title {
    font-size: 1.1rem;
  }

  .delete {
    height: 24px;
    width: 24px;
    max-height: 24px;
    max-width: 24px;
  }
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
}
</style>
